import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockInvoiceService } from '@/services/mock/MockInvoiceService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { InvoiceRecord, InvoiceException } from '@/types/domain/invoice-management';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatInr, formatDate } from '@/lib/utils';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import { usePermission } from '@/hooks/usePermission';
import { getAvailableInvoiceActions } from '@/features/invoices/utils/invoiceTransitions';
import {
  ArrowLeft,
  Receipt,
  Scale,
  ShieldAlert,
  Database,
  FileText,
  MessageSquare,
  History,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  IndianRupee,
  Copy,
  Clock,
  XCircle,
  RefreshCw,
  CreditCard,
} from 'lucide-react';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';
import { InvoiceAuditHistoryTab } from '@/features/invoices/components/InvoiceAuditHistoryTab';
import { InvoiceExceptionModal } from '@/features/invoices/components/InvoiceExceptionModal';
import { InvoiceSapSyncModal } from '@/features/invoices/components/InvoiceSapSyncModal';
import { evaluatePaymentReadiness } from '@/features/invoices/utils/paymentReadiness';

type InvoiceDetailTab =
  | 'OVERVIEW'
  | 'ITEMS'
  | 'MATCH'
  | 'EXCEPTIONS'
  | 'PAYMENT'
  | 'DUPLICATE'
  | 'SAP'
  | 'DOCUMENTS'
  | 'COMMENTS'
  | 'AUDIT';

export interface InvoiceDetailPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const InvoiceDetailPage: React.FC<InvoiceDetailPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const { can, user } = usePermission();

  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals & Action States
  const [selectedException, setSelectedException] = useState<InvoiceException | null>(null);
  const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
  const [isSapModalOpen, setIsSapModalOpen] = useState(false);
  const [isSyncingSap, setIsSyncingSap] = useState(false);
  const [isMatching, setIsMatching] = useState(false);

  const [activeTab, setActiveTab] = useState<InvoiceDetailTab>('OVERVIEW');

  const loadData = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const [record, docs, comms] = await Promise.all([
        mockInvoiceService.getInvoiceById(id),
        mockDocumentCommentService.getDocuments('INVOICE', id),
        mockDocumentCommentService.getComments('INVOICE', id),
      ]);

      setInvoice(record);
      setDocuments(docs);
      setComments(comms);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadData();
  }, [id]);

  const handleRunMatch = async () => {
    if (!invoice) return;
    try {
      setIsMatching(true);
      await mockInvoiceService.runThreeWayMatch(invoice.id);
      await loadData();
    } finally {
      setIsMatching(false);
    }
  };

  const handleApprove = async () => {
    if (!invoice) return;
    await mockInvoiceService.approveInvoice(invoice.id, 'Formal Accounts Payable approval granted.');
    await loadData();
  };

  const handleAuthorizePayment = async () => {
    if (!invoice) return;
    await mockInvoiceService.markReadyForPayment(invoice.id);
    await loadData();
  };

  const handleHold = async () => {
    if (!invoice) return;
    await mockInvoiceService.placeOnHold(invoice.id, 'Accounts Payable verification hold applied.');
    await loadData();
  };

  const handleReleaseHold = async () => {
    if (!invoice) return;
    await mockInvoiceService.releaseFromHold(invoice.id, 'Hold released following documentation check.');
    await loadData();
  };

  const handleSapSync = async () => {
    if (!invoice) return;
    try {
      setIsSyncingSap(true);
      await mockInvoiceService.syncWithSapMiro(invoice.id);
      setIsSapModalOpen(false);
      await loadData();
    } finally {
      setIsSyncingSap(false);
    }
  };

  const handleReject = async () => {
    if (!invoice) return;
    await mockInvoiceService.rejectInvoice(invoice.id, 'Commercial discrepancy rejected by Finance.');
    await loadData();
  };

  const handleCancel = async () => {
    if (!invoice) return;
    await mockInvoiceService.cancelInvoice(invoice.id, 'Administrative cancellation.');
    await loadData();
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        <Receipt className="h-8 w-8 animate-bounce mx-auto text-primary mb-2" />
        Loading Accounts Payable Command Center...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-16 text-center space-y-3 font-poppins">
        <h2 className="text-lg font-extrabold text-foreground">Invoice Not Found</h2>
        <p className="text-xs text-foreground-secondary">The requested invoice record does not exist or has been archived.</p>
        <Button onClick={() => navigate(ROUTES.INVOICES.LIST)} className="rounded-xl font-bold font-poppins cursor-pointer">
          Return to Invoices List
        </Button>
      </div>
    );
  }

  const availableActions = getAvailableInvoiceActions(invoice, { can, role: user?.role });
  const isMatchAvailable = availableActions.find((a) => a.action === 'EXECUTE_MATCH')?.isAvailable;
  const isApproveAvailable = availableActions.find((a) => a.action === 'APPROVE')?.isAvailable;
  const isAuthorizeAvailable = availableActions.find((a) => a.action === 'MARK_READY_FOR_PAYMENT')?.isAvailable;
  const isHoldAvailable = availableActions.find((a) => a.action === 'PLACE_ON_HOLD')?.isAvailable;
  const isReleaseAvailable = availableActions.find((a) => a.action === 'RELEASE_FROM_HOLD')?.isAvailable;
  const isSyncSapAvailable = availableActions.find((a) => a.action === 'SYNC_SAP_MIRO')?.isAvailable;
  const isRejectAvailable = availableActions.find((a) => a.action === 'REJECT')?.isAvailable;
  const isCancelAvailable = availableActions.find((a) => a.action === 'CANCEL')?.isAvailable;

  const readiness = evaluatePaymentReadiness(invoice);

  return (
    <div ref={pageRef} className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-24 max-w-7xl mx-auto'}`}>
      {/* Top Header & Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.INVOICES.LIST)}
              className="h-9 rounded-xl border-border font-bold text-xs font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Invoices
            </Button>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xl font-black text-foreground tracking-tight font-poppins">{invoice.invoiceNumber}</span>
            <span className="text-xs font-mono font-bold text-slate-500 bg-surface px-2 py-0.5 rounded border border-border">
              Ref: {invoice.vendorInvoiceNumber}
            </span>
            {invoice.sapMiroNumber && (
              <span className="font-mono text-xs font-bold text-purple-600 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20 flex items-center gap-1">
                <Database className="h-3 w-3" /> SAP MIRO: {invoice.sapMiroNumber}
              </span>
            )}
            {invoice.duplicateRisk === 'DUPLICATE_CONFIRMED' && (
              <span className="text-xs font-bold text-rose-700 bg-rose-500/10 px-2 py-0.5 rounded border border-rose-500/20 flex items-center gap-1">
                <Copy className="h-3.5 w-3.5 text-rose-600" /> Duplicate Detected
              </span>
            )}
            {invoice.isOverdue && (
              <span className="text-xs font-bold text-rose-700 bg-rose-500/10 px-2 py-0.5 rounded border border-rose-500/20 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-rose-600" /> {invoice.daysOverdue}d Overdue
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {isMatchAvailable && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunMatch}
              disabled={isMatching}
              className="rounded-xl font-bold border-border text-xs"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isMatching ? 'animate-spin' : ''}`} />
              Run 3-Way Match
            </Button>
          )}

          {isApproveAvailable && (
            <Button
              size="sm"
              onClick={handleApprove}
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs text-xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve Invoice
            </Button>
          )}

          {isAuthorizeAvailable && (
            <Button
              size="sm"
              onClick={handleAuthorizePayment}
              className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-2xs text-xs"
            >
              <IndianRupee className="h-3.5 w-3.5 mr-1.5" /> Authorize for Payment
            </Button>
          )}

          {isHoldAvailable && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleHold}
              className="rounded-xl font-bold border-amber-500/40 text-amber-700 hover:bg-amber-50 text-xs"
            >
              <AlertTriangle className="h-3.5 w-3.5 mr-1.5 text-amber-600" /> Place on Hold
            </Button>
          )}

          {isReleaseAvailable && (
            <Button
              size="sm"
              onClick={handleReleaseHold}
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Release Hold
            </Button>
          )}

          {isSyncSapAvailable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSapModalOpen(true)}
              className="rounded-xl font-bold border-purple-500/30 bg-purple-500/5 text-purple-700 hover:bg-purple-500/10 text-xs"
            >
              <Database className="h-3.5 w-3.5 mr-1.5" /> Post SAP MIRO
            </Button>
          )}

          {isRejectAvailable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReject}
              className="rounded-xl font-bold text-rose-600 hover:bg-rose-50 text-xs"
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
          )}

          {isCancelAvailable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="rounded-xl font-bold text-slate-500 hover:bg-surface text-xs"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Main Command Center Header Card */}
      <Card className="rounded-2xl border-border bg-card shadow-2xs overflow-hidden">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-primary">
                  Accounts Payable Claim
                </span>
                <span
                  className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${
                    invoice.status === 'APPROVED' || invoice.status === 'PAID'
                      ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                      : invoice.status === 'EXCEPTION'
                      ? 'bg-rose-500/10 text-rose-700 border-rose-500/20'
                      : 'bg-blue-500/10 text-blue-700 border-blue-500/20'
                  }`}
                >
                  {invoice.status}
                </span>
              </div>
              <h2 className="text-xl font-black text-foreground">{invoice.vendorName}</h2>
              <p className="text-xs text-foreground-secondary">
                Vendor Code: <span className="font-mono font-bold text-foreground">{invoice.vendorCode}</span> | Plant: {invoice.plantCode}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="p-3 rounded-xl bg-surface border border-border/70 text-right">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase block">Total Billed Claim</span>
                <span className="text-xl font-black text-foreground font-mono">
                  {formatInr(invoice.totalAmount)}
                </span>
                <span className="text-[10px] text-foreground-muted font-medium block">
                  Tax: {formatInr(invoice.taxAmount)}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-surface border border-border/70 text-right">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase block">3-Way Match Verdict</span>
                <span className={`text-sm font-black block ${
                  invoice.matchingStatus === 'MATCHED' ? 'text-emerald-600' : 'text-rose-600'
                }`}>
                  {invoice.matchingStatus.replace(/_/g, ' ')}
                </span>
                <span className="text-[10px] text-foreground-muted block">
                  {invoice.exceptions.filter((e) => e.status !== 'RESOLVED').length} open exception(s)
                </span>
              </div>
            </div>
          </div>

          {/* Traceability Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div
              onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(invoice.poId))}
              className="p-3 rounded-xl bg-surface hover:bg-surface/80 border border-border cursor-pointer transition-colors space-y-1"
            >
              <span className="text-[10px] font-bold text-foreground-muted uppercase block flex items-center justify-between">
                <span>1. Authorized PO</span>
                <ExternalLink className="h-3 w-3" />
              </span>
              <span className="font-bold text-primary block">{invoice.poNumber}</span>
              <span className="text-[10px] text-emerald-600 font-semibold">Contract Terms</span>
            </div>

            <div
              onClick={() => {
                if (invoice.grnId) navigate(ROUTES.GRN.DETAILS(invoice.grnId));
              }}
              className={`p-3 rounded-xl bg-surface border border-border space-y-1 ${
                invoice.grnId ? 'hover:bg-surface/80 cursor-pointer' : ''
              }`}
            >
              <span className="text-[10px] font-bold text-foreground-muted uppercase block flex items-center justify-between">
                <span>2. Warehouse GRN</span>
                {invoice.grnId && <ExternalLink className="h-3 w-3" />}
              </span>
              <span className={`font-bold block ${invoice.grnNumber ? 'text-emerald-600' : 'text-rose-600'}`}>
                {invoice.grnNumber || 'Missing GRN'}
              </span>
              <span className="text-[10px] text-foreground-secondary font-semibold">
                {invoice.grnNumber ? 'Accepted Physical Goods' : 'Inwarding Required'}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-surface border border-border space-y-1">
              <span className="text-[10px] font-bold text-foreground-muted uppercase block">
                3. Payment Readiness
              </span>
              <span className="font-bold text-foreground block">{invoice.paymentStatus.replace(/_/g, ' ')}</span>
              <span className={`text-[10px] font-semibold ${
                readiness.isReadyForPayment ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {readiness.isReadyForPayment ? 'All 6 Checks Passed' : `${readiness.blockingReasons.length} Check(s) Pending`}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-surface border border-border space-y-1">
              <span className="text-[10px] font-bold text-foreground-muted uppercase block">
                4. Settlement Due Date
              </span>
              <span className={`font-bold block ${invoice.isOverdue ? 'text-rose-600' : 'text-foreground'}`}>
                {formatDate(invoice.dueDate)}
              </span>
              <span className="text-[10px] text-foreground-secondary font-semibold">
                {invoice.paymentTerms}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto pb-1 text-xs">
        {(
          [
            { id: 'OVERVIEW', label: 'Commercial Overview', icon: FileText },
            { id: 'ITEMS', label: `Line Items (${invoice.items.length})`, icon: Receipt },
            { id: 'MATCH', label: '3-Way Match Audit', icon: Scale },
            { id: 'EXCEPTIONS', label: `Exceptions (${invoice.exceptions.length})`, icon: ShieldAlert },
            { id: 'PAYMENT', label: 'Payment Readiness', icon: IndianRupee },
            { id: 'DUPLICATE', label: 'Duplicate Risk', icon: Copy },
            { id: 'SAP', label: 'SAP S/4HANA MIRO', icon: Database },
            { id: 'DOCUMENTS', label: `Documents (${documents.length})`, icon: FileText },
            { id: 'COMMENTS', label: `Comments (${comments.length})`, icon: MessageSquare },
            { id: 'AUDIT', label: 'Audit Trail', icon: History },
          ] as const
        ).map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-primary/10 text-primary font-black border border-primary/20 shadow-2xs'
                  : 'text-foreground-secondary hover:text-foreground font-bold hover:bg-surface'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content 1: OVERVIEW */}
      {activeTab === 'OVERVIEW' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-2 rounded-2xl border-border">
            <CardContent className="p-6 space-y-6">
              <div className="border-b border-border/60 pb-3">
                <h3 className="text-sm font-extrabold text-foreground">Accounts Payable Commercial Summary</h3>
                <p className="text-xs text-foreground-secondary">
                  Vendor invoice identification, commercial dates, and financial figures reconciliation.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Vendor Invoice Reference:</span>
                  <p className="font-mono font-bold text-foreground">{invoice.vendorInvoiceNumber}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Invoice Date:</span>
                  <p className="font-extrabold text-foreground">{formatDate(invoice.invoiceDate)}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Dock Received Date:</span>
                  <p className="font-extrabold text-foreground">{formatDate(invoice.receivedDate)}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Payment Due Date:</span>
                  <p className={`font-mono font-bold ${invoice.isOverdue ? 'text-rose-600' : 'text-foreground'}`}>
                    {formatDate(invoice.dueDate)} {invoice.isOverdue ? `(${invoice.daysOverdue}d overdue)` : ''}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Payment Terms:</span>
                  <p className="font-bold text-foreground">{invoice.paymentTerms}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Intake Source Channel:</span>
                  <p className="font-bold text-foreground">{invoice.source}</p>
                </div>

                <div className="sm:col-span-2 space-y-1 pt-2 border-t border-border/40">
                  <span className="text-foreground-secondary font-medium">Invoice Remarks & Notes:</span>
                  <p className="text-foreground font-medium bg-surface p-3 rounded-xl border border-border/60">
                    {invoice.notes || 'No special remarks provided by vendor.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="rounded-2xl border-border">
              <CardContent className="p-6 space-y-3 text-xs">
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <IndianRupee className="h-4 w-4 text-emerald-600" /> Commercial Breakdown
                </h3>
                <div className="space-y-2 pt-1">
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Subtotal (Net):</span>
                    <span className="font-mono font-bold text-foreground">
                      {formatInr(invoice.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Tax ({invoice.taxPercent}%):</span>
                    <span className="font-mono font-bold text-foreground">
                      {formatInr(invoice.taxAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Shipping / Freight:</span>
                    <span className="font-mono text-foreground">
                      {formatInr(invoice.shippingAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Discounts Applied:</span>
                    <span className="font-mono text-rose-600">
                      - {formatInr(invoice.discountAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-border/60 text-sm font-black text-primary">
                    <span>Grand Total:</span>
                    <span className="font-mono">
                      {formatInr(invoice.totalAmount)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border">
              <CardContent className="p-6 space-y-3 text-xs">
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <Database className="h-4 w-4 text-purple-600" /> ERP MIRO Status
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">SAP MIRO Voucher:</span>
                    <span className="font-mono font-bold text-purple-700">
                      {invoice.sapMiroNumber || 'Pending Posting'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Posting State:</span>
                    <span className="font-bold text-foreground">{invoice.sapSyncStatus}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Tab Content 2: ITEMS */}
      {activeTab === 'ITEMS' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-4">
            <div className="border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground">Line Item 3-Way Reconciliation</h3>
              <p className="text-xs text-foreground-secondary">
                Detailed comparison of PO ordered quantity & price vs GRN accepted quantity vs Billed claims.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse font-heading">
                <thead>
                  <tr className="border-b border-border/80 text-[10px] font-extrabold text-foreground-secondary uppercase">
                    <th className="py-2.5 px-3">#</th>
                    <th className="py-2.5 px-3">Material / Description</th>
                    <th className="py-2.5 px-3 text-right">PO Qty</th>
                    <th className="py-2.5 px-3 text-right text-emerald-700">GRN Accepted</th>
                    <th className="py-2.5 px-3 text-right text-primary">Billed Qty</th>
                    <th className="py-2.5 px-3 text-right">PO Price</th>
                    <th className="py-2.5 px-3 text-right text-primary">Billed Price</th>
                    <th className="py-2.5 px-3 text-right">Line Total</th>
                    <th className="py-2.5 px-3">Match Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {invoice.items.map((it, idx) => (
                    <tr key={it.id || idx} className="hover:bg-surface/50 transition-colors">
                      <td className="py-3 px-3 font-mono text-slate-400">{it.lineNumber}</td>
                      <td className="py-3 px-3">
                        <span className="font-bold text-foreground block">{it.description}</span>
                        <span className="text-[10px] text-foreground-muted font-mono">SKU: {it.materialCode}</span>
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-foreground-secondary">{it.poQuantity}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-emerald-700">{it.grnQuantity}</td>
                      <td className={`py-3 px-3 text-right font-mono font-black ${
                        it.quantity !== it.grnQuantity ? 'text-rose-600' : 'text-primary'
                      }`}>
                        {it.quantity}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-foreground-secondary">
                        {formatInr(it.poUnitPrice)}
                      </td>
                      <td className={`py-3 px-3 text-right font-mono font-black ${
                        it.unitPrice > it.poUnitPrice ? 'text-rose-600' : 'text-foreground'
                      }`}>
                        {formatInr(it.unitPrice)}
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-foreground">
                        {formatInr(it.lineTotal)}
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                            it.matchingStatus === 'MATCHED'
                              ? 'bg-emerald-500/10 text-emerald-700'
                              : 'bg-rose-500/10 text-rose-700'
                          }`}
                        >
                          {it.matchingStatus.replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 3: MATCH */}
      {activeTab === 'MATCH' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                <Scale className="h-4 w-4 text-purple-600" /> 3-Way Match Audit Diagram
              </h3>
              <p className="text-xs text-foreground-secondary">
                Commercial and physical quantity matching prior to invoice payment release.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-heading">
              <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-1.5">
                <span className="text-[10px] font-extrabold text-primary uppercase block">1. PO Authorized Commitment</span>
                <span className="font-mono text-xl font-black text-foreground block">
                  {formatInr(invoice.totalAmount)}
                </span>
                <span className="text-[10px] text-foreground-muted block font-mono">PO #{invoice.poNumber}</span>
              </div>

              <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-1.5">
                <span className="text-[10px] font-extrabold text-emerald-700 uppercase block">2. GRN Accepted Stock</span>
                <span className="font-mono text-xl font-black text-emerald-600 block">
                  {invoice.grnNumber ? formatInr(invoice.totalAmount) : '₹0 (Missing)'}
                </span>
                <span className="text-[10px] text-foreground-muted block font-mono">GRN #{invoice.grnNumber || 'None'}</span>
              </div>

              <div className="p-4 rounded-xl border border-purple-500/20 bg-purple-500/5 space-y-1.5">
                <span className="text-[10px] font-extrabold text-purple-700 uppercase block">3. Vendor Billed Claim</span>
                <span className="font-mono text-xl font-black text-purple-600 block">
                  {formatInr(invoice.totalAmount)}
                </span>
                <span className="text-[10px] text-foreground-muted block font-mono">Ref: {invoice.vendorInvoiceNumber}</span>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-border bg-surface text-xs space-y-2">
              <span className="font-bold text-foreground block">Reconciliation Assessment:</span>
              <p className="text-foreground-secondary leading-relaxed">
                {invoice.matchingStatus === 'MATCHED'
                  ? 'All quantities and unit prices strictly reconcile between the PO contract, warehouse accepted inventory, and the vendor claim. Match passed 100%.'
                  : `Discrepancy detected: ${invoice.matchingStatus.replace(/_/g, ' ')}. Review active exceptions below before releasing payment.`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 4: EXCEPTIONS */}
      {activeTab === 'EXCEPTIONS' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-rose-600" /> Active Exceptions & Discrepancies
              </h3>
              <p className="text-xs text-foreground-secondary">
                Commercial issues preventing automated payment authorization. Authorized users may resolve exceptions.
              </p>
            </div>

            {invoice.exceptions.length === 0 ? (
              <div className="p-8 text-center text-xs text-emerald-700 font-bold bg-emerald-500/5 rounded-xl border border-emerald-500/20">
                Zero exceptions detected on this invoice. Full compliance confirmed.
              </div>
            ) : (
              <div className="space-y-3">
                {invoice.exceptions.map((ex) => (
                  <div
                    key={ex.id}
                    className="p-4 rounded-xl border border-border bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-rose-600 font-mono">{ex.code}</span>
                        <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded ${
                          ex.severity === 'BLOCKING' ? 'bg-rose-500/10 text-rose-700' : 'bg-amber-500/10 text-amber-700'
                        }`}>
                          {ex.severity}
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          ex.status === 'RESOLVED' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-slate-500/10 text-slate-700'
                        }`}>
                          {ex.status}
                        </span>
                      </div>
                      <p className="text-foreground font-medium">{ex.description}</p>
                      {ex.resolutionNotes && (
                        <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                          Resolution: {ex.resolutionNotes} (By {ex.resolvedBy} on {formatDate(ex.resolvedAt!)})
                        </p>
                      )}
                    </div>

                    {ex.status !== 'RESOLVED' && can('INVOICE_MATCH') && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedException(ex);
                          setIsExceptionModalOpen(true);
                        }}
                        className="rounded-xl font-bold bg-primary text-white text-xs whitespace-nowrap"
                      >
                        Resolve Exception
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab Content 5: PAYMENT */}
      {activeTab === 'PAYMENT' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3">
              <div>
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <IndianRupee className="h-4 w-4 text-purple-600" /> Payment Readiness Gating Matrix
                </h3>
                <p className="text-xs text-foreground-secondary">
                  Strict validation of 6 core prerequisites prior to automated disbursement in payment runs.
                </p>
              </div>

              <div className="flex items-center gap-2">
                {invoice.isReadyForPayment && invoice.paymentStatus !== 'READY_FOR_PAYMENT' && (
                  <Button
                    size="sm"
                    onClick={handleAuthorizePayment}
                    className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white text-xs font-poppins cursor-pointer"
                  >
                    <IndianRupee className="h-3.5 w-3.5 mr-1.5" /> Authorize for Payment
                  </Button>
                )}
                {invoice.isReadyForPayment && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(ROUTES.PAYMENTS.LIST)}
                    className="rounded-xl font-bold text-xs"
                  >
                    <CreditCard className="h-3.5 w-3.5 mr-1.5" /> Treasury Disbursements
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {readiness.checklist.map((item) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-xl border flex items-center justify-between gap-3 text-xs ${
                    item.passed
                      ? 'border-emerald-500/20 bg-emerald-500/5'
                      : 'border-rose-500/20 bg-rose-500/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {item.passed ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-rose-600 shrink-0" />
                    )}
                    <div>
                      <span className="font-extrabold text-foreground block">{item.label}</span>
                      <span className="text-[11px] text-foreground-secondary">{item.details}</span>
                    </div>
                  </div>

                  <span className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full ${
                    item.passed ? 'bg-emerald-500/10 text-emerald-700' : 'bg-rose-500/10 text-rose-700'
                  }`}>
                    {item.passed ? 'PASSED' : 'BLOCKED'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 6: DUPLICATE */}
      {activeTab === 'DUPLICATE' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                <Copy className="h-4 w-4 text-primary" /> Duplicate Invoice Risk Analysis
              </h3>
              <p className="text-xs text-foreground-secondary">
                Cross-matching of vendor ID, normalized invoice number string, and PO amount against 500+ records.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase">Duplicate Risk State</span>
                <p className={`font-mono text-lg font-black ${
                  invoice.duplicateRisk === 'NO_DUPLICATE' ? 'text-emerald-600' : 'text-rose-600'
                }`}>
                  {invoice.duplicateRisk}
                </p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase">Normalized Key</span>
                <p className="font-mono text-xs font-bold text-foreground">{invoice.vendorInvoiceNumber.toUpperCase().replace(/[/\\_]/g, '-')}</p>
                <span className="text-[10px] text-foreground-muted">Punctuation & whitespace normalized</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase">Original Match Record</span>
                <p className="font-mono text-sm font-bold text-primary">{invoice.duplicateDetails?.originalInvoiceNumber || 'None'}</p>
                <span className="text-[10px] text-foreground-muted">{invoice.duplicateDetails?.reason || 'Clean Record'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 7: SAP */}
      {activeTab === 'SAP' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-3">
              <div>
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <Database className="h-4 w-4 text-purple-600" /> SAP S/4HANA MIRO Invoice Verification Simulator
                </h3>
                <p className="text-xs text-foreground-secondary">
                  Bi-directional replication of vendor invoice into SAP ERP ledger via simulated BAPI `BAPI_INCOMINGINVOICE_CREATE`.
                </p>
              </div>

              {invoice.sapSyncStatus !== 'SYNCED' && (
                <Button
                  size="sm"
                  onClick={() => setIsSapModalOpen(true)}
                  className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-2xs text-xs"
                >
                  <Database className="h-3.5 w-3.5 mr-1.5" /> Post to SAP MIRO
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">SAP Document Number</span>
                <p className="font-mono text-base font-black text-purple-700">{invoice.sapMiroNumber || 'PENDING GENERATION'}</p>
                <span className="text-[10px] text-foreground-muted">10-digit SAP financial document</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">Posting State</span>
                <p className="font-mono text-base font-black text-foreground">{invoice.sapSyncStatus}</p>
                <span className="text-[10px] text-foreground-muted">Verified MIRO invoice status</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">SAP Target Company</span>
                <p className="font-mono text-base font-black text-foreground">Company Code {invoice.companyCode}</p>
                <span className="text-[10px] text-foreground-muted">Plant: {invoice.plantCode}</span>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-border/80 bg-slate-900 text-slate-100 font-mono text-xs overflow-x-auto space-y-2">
              <span className="text-slate-400 text-[10px] uppercase font-bold block">// BAPI_INCOMINGINVOICE_CREATE Schema</span>
              <pre className="text-[11px] leading-relaxed">
{JSON.stringify(
  {
    HEADER: {
      DOC_TYPE: 'RE',
      DOC_DATE: invoice.invoiceDate,
      PSTNG_DATE: invoice.receivedDate,
      REF_DOC_NO: invoice.vendorInvoiceNumber,
      COMP_CODE: invoice.companyCode,
      GROSS_AMOUNT: invoice.totalAmount,
      CURRENCY: invoice.currency,
    },
    ITEMS: invoice.items.map((it) => ({
      ITEM_NO: it.lineNumber,
      PO_NUMBER: invoice.poNumber,
      PO_ITEM: it.lineNumber,
      AMOUNT: it.lineTotal,
      QUANTITY: it.quantity,
      PO_UNIT: it.uom,
    })),
  },
  null,
  2
)}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 8: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <DocumentManagementPanel
          documents={documents}
          onUploadFile={async (file, category) => {
            if (!invoice) return;
            const newDoc = await mockDocumentCommentService.uploadDocument('INVOICE', invoice.id, file, category);
            setDocuments((prev) => [newDoc, ...prev]);
          }}
          onDownloadFile={async (docId) => {
            await mockDocumentCommentService.downloadDocument(docId);
          }}
          onDeleteFile={async (docId) => {
            await mockDocumentCommentService.deleteDocument(docId);
            setDocuments((prev) => prev.filter((d) => d.id !== docId));
          }}
        />
      )}

      {/* Tab Content 9: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <EnterpriseCommentThread
          comments={comments}
          onAddComment={async (text, attachmentIds) => {
            if (!invoice) return;
            const newComment = await mockDocumentCommentService.createComment(
              'INVOICE',
              invoice.id,
              text,
              attachmentIds
            );
            setComments((prev) => [...prev, newComment]);
          }}
          onDeleteComment={async (commentId) => {
            await mockDocumentCommentService.deleteComment(commentId);
            setComments((prev) => prev.filter((c) => c.id !== commentId));
          }}
        />
      )}

      {/* Tab Content 10: AUDIT */}
      {activeTab === 'AUDIT' && <InvoiceAuditHistoryTab auditHistory={invoice.auditHistory} />}

      {/* Modals */}
      <InvoiceExceptionModal
        isOpen={isExceptionModalOpen}
        onClose={() => {
          setIsExceptionModalOpen(false);
          setSelectedException(null);
        }}
        exception={selectedException}
        invoice={invoice}
        onResolve={async (exId, notes) => {
          await mockInvoiceService.resolveException(invoice.id, exId, notes);
          await loadData();
        }}
      />

      <InvoiceSapSyncModal
        isOpen={isSapModalOpen}
        onClose={() => setIsSapModalOpen(false)}
        onConfirmSync={handleSapSync}
        invoice={invoice}
        isSubmitting={isSyncingSap}
      />
    </div>
  );
};
